import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { Compass, Flame, Heart, Home, MapPin, MessageSquare, Search, Trophy, UtensilsCrossed, Wallet } from "lucide-react";

type SearchHit = {
  id: string;
  type: "deal" | "restaurant";
  title: string;
  subtitle?: string;
};

const STATIC_PAGES: { label: string; to: string; icon: any; keywords: string }[] = [
  { label: "Startseite", to: "/home", icon: Home, keywords: "home start deals" },
  { label: "Restaurants", to: "/restaurants", icon: UtensilsCrossed, keywords: "restaurants meine" },
  { label: "FoodGram", to: "/social", icon: Compass, keywords: "social foodgram feed" },
  { label: "Mein Wallet", to: "/guest-dashboard", icon: Wallet, keywords: "wallet konto profil" },
  { label: "Nachrichten", to: "/messages", icon: MessageSquare, keywords: "messages chat" },
  { label: "Favoriten", to: "/favorites", icon: Heart, keywords: "favoriten merkliste" },
  { label: "Loyalty", to: "/loyalty-info", icon: Trophy, keywords: "treue punkte" },
  { label: "Karte / Suche", to: "/search", icon: MapPin, keywords: "karte suche map" },
];

/**
 * Global Cmd/Ctrl+K command palette for guests & merchants.
 * Searches deals + restaurants + static pages.
 */
const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      const [{ data: deals }, { data: restaurants }] = await Promise.all([
        supabase
          .from("deals")
          .select("id, title, restaurant_id")
          .ilike("title", `%${query}%`)
          .eq("status", "ACTIVE")
          .limit(6),
        supabase
          .from("restaurants_public")
          .select("id, name, city, cuisine_type")
          .ilike("name", `%${query}%`)
          .limit(6),
      ]);
      if (cancelled) return;
      const dealHits: SearchHit[] = (deals || []).map((d: any) => ({
        id: d.id,
        type: "deal",
        title: d.title,
      }));
      const restaurantHits: SearchHit[] = (restaurants || []).map((r: any) => ({
        id: r.id,
        type: "restaurant",
        title: r.name,
        subtitle: [r.cuisine_type, r.city].filter(Boolean).join(" · "),
      }));
      setHits([...restaurantHits, ...dealHits]);
    };
    const t = setTimeout(run, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const go = (to: string) => {
    setOpen(false);
    setQuery("");
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Suche Restaurants, Deals oder Seiten… (⌘K)"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>Keine Treffer.</CommandEmpty>

        {hits.length > 0 && (
          <>
            <CommandGroup heading="Treffer">
              {hits.map((h) => (
                <CommandItem
                  key={`${h.type}-${h.id}`}
                  value={`${h.type}-${h.id}-${h.title}`}
                  onSelect={() =>
                    go(h.type === "deal" ? `/deal/${h.id}` : `/restaurant/${h.id}`)
                  }
                >
                  {h.type === "deal" ? (
                    <Flame className="mr-2 h-4 w-4 text-orange-500" />
                  ) : (
                    <UtensilsCrossed className="mr-2 h-4 w-4 text-violet-500" />
                  )}
                  <div className="flex flex-col">
                    <span className="font-semibold">{h.title}</span>
                    {h.subtitle && (
                      <span className="text-xs text-muted-foreground">{h.subtitle}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Schnellnavigation">
          {STATIC_PAGES.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem
                key={p.to}
                value={`${p.label} ${p.keywords}`}
                onSelect={() => go(p.to)}
              >
                <Icon className="mr-2 h-4 w-4 text-primary" />
                {p.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
